import React, { useState } from 'react';
import { FaTimes, FaUpload } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { STRUCTURING_API } from './structuringUtils';

const MAX_FILES = 10;

interface AddFilesModalProps {
    isOpen: boolean;
    onClose: () => void;
    operationId: string;
    onComplete: () => void;
}

const AddFilesModal: React.FC<AddFilesModalProps> = ({ isOpen, onClose, operationId, onComplete }) => {
    const [files, setFiles] = useState<File[]>([]);
    const [isUploading, setIsUploading] = useState(false);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = Array.from(e.target.files || []);
        const total = files.length + selected.length;
        if (total > MAX_FILES) {
            toast.error(`Máximo de ${MAX_FILES} arquivos permitidos`);
            return;
        }
        setFiles(prev => [...prev, ...selected]);
    };

    const handleRemoveFile = (index: number) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleUpload = async () => {
        if (files.length === 0) {
            toast.error('Selecione ao menos um arquivo');
            return;
        }

        setIsUploading(true);
        try {
            const token = localStorage.getItem('authToken');
            const formData = new FormData();
            files.forEach(file => formData.append('files', file));

            const response = await fetch(`${STRUCTURING_API.url}/operations/${operationId}/add-files`, {
                method: 'POST',
                headers: {
                    'x-api-key': STRUCTURING_API.key || '',
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
            });

            if (response.ok) {
                toast.success(`${files.length} arquivo(s) enviado(s) com sucesso! Recarregando em 5s...`);
                setFiles([]);
                onClose();
                await new Promise(resolve => setTimeout(resolve, 5000));
                onComplete();
            } else {
                const data = await response.json().catch(() => ({}));
                toast.error(data.message || 'Erro ao enviar arquivos');
            }
        } catch (error) {
            toast.error('Erro de conexão ao enviar arquivos');
        } finally {
            setIsUploading(false);
        }
    };

    const handleClose = () => {
        setFiles([]);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-800 rounded-lg w-full max-w-md flex flex-col">
                <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold dark:text-white">Adicionar Arquivos XML</h3>
                    <button onClick={handleClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                        <FaTimes size={18} />
                    </button>
                </div>

                <div className="p-4">
                    <label
                        htmlFor="add-xml-files"
                        className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    >
                        <div className="flex flex-col items-center">
                            <FaUpload className="w-5 h-5 mb-1 text-gray-500 dark:text-gray-400" />
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Selecionar arquivos XML (máx. {MAX_FILES})
                            </p>
                        </div>
                        <input
                            id="add-xml-files"
                            type="file"
                            className="hidden"
                            accept=".xml"
                            multiple
                            disabled={isUploading}
                            onChange={handleFileChange}
                        />
                    </label>

                    {files.length > 0 && (
                        <div className="mt-3 max-h-48 overflow-y-auto">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{files.length} arquivo(s) selecionado(s)</p>
                            <ul className="space-y-1">
                                {files.map((file, i) => (
                                    <li key={i} className="flex justify-between items-center text-sm bg-gray-50 dark:bg-slate-700 px-3 py-1.5 rounded">
                                        <span className="text-gray-700 dark:text-gray-200 truncate mr-2">{file.name}</span>
                                        <button onClick={() => handleRemoveFile(i)} className="text-red-500 hover:text-red-700 text-xs flex-shrink-0">
                                            <FaTimes />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
                    <button onClick={handleClose} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-300 rounded text-sm">
                        Cancelar
                    </button>
                    <button
                        onClick={handleUpload}
                        disabled={isUploading || files.length === 0}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white rounded text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <FaUpload /> {isUploading ? 'Enviando...' : `Enviar (${files.length})`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddFilesModal;
